import { Dialog } from '@e3dc-react/shell/Elements/Dialog'
import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { refreshApiView } from '@e3dc-react/shell/State/ApiView/ApiViewActions'
import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Formik } from 'formik'
import ReplaceFileForm from './ReplaceFileForm'
import { mixed, object } from 'yup'
import { replaceFile } from '../../api'
import { DownloadFileItem } from '../../models'

export interface ReplaceFileDialogProps {
  item: DownloadFileItem
  onClose?: () => void
  closeEditModal?: () => void
}

export interface ReplaceFileFormValues {
  file: File | null
}

/**
 * Dialog to replace files to the documents feature
 */
const ReplaceFileDialog: React.FC<ReplaceFileDialogProps> = ({ item, closeEditModal, onClose }) => {
  const { t } = useCustomTranslation()
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()
  const authFetch = useAuthFetchWithResult()
  const dispatch = useDispatch()
  const [file, setFile] = useState<File | undefined>(undefined)

  const onReplaceFile = async (values: ReplaceFileFormValues): Promise<void> => {
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)

    replaceFile(item.id, formData, authFetch)
      .then(() => enqueueSuccessStack(t('documents.upload.success')))
      .catch(error => {
        enqueueErrorStack(t('documents.upload.error'))
        console.error('Error uploading file:', error)
      })
      .finally(() => dispatch(refreshApiView()))
    onClose?.()
    closeEditModal?.()
  }

  const initialValues = {
    file: null,
  }

  const validationSchema = object().shape({
    file: mixed().required('File is required'),
  })

  return (
    <Formik initialValues={initialValues} onSubmit={onReplaceFile} validationSchema={validationSchema}>
      {({ handleSubmit }) => {
        return (
          <Dialog
            maxWidth="xs"
            title={t('documents.upload.form.title')}
            onClose={onClose}
            actions={{
              primary: {
                name: t('documents.upload.button'),
                onClick: () => handleSubmit(),
              },
              secondary: {
                name: t('common.cancel'),
                onClick: () => onClose?.(),
              },
            }}
          >
            <ReplaceFileForm item={item} setUploadedFile={setFile} />
          </Dialog>
        )
      }}
    </Formik>
  )
}

export default ReplaceFileDialog
